import { TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { NextActionAlert } from '../NextActionAlert';
import type { ComposedPrerequisites } from './prerequisites';

/**
 * What the module cannot be USED without, stated before the reader tries — reference §3.
 *
 * **Amber**, because something is genuinely in the way: this is not the slate "nothing here
 * yet" of an empty list. It names only what is MISSING, so it disappears the moment the last
 * catalog is filled rather than nagging about work already done.
 *
 * **ONE requirement at a time** — see `composePrerequisites`, which decides which. This draws
 * what that composed; it does not get to compose it differently, which is what keeps the two
 * shells rendering the same banner.
 *
 * Its own wrapper carries the gap. The folder tabs below deliberately have none, because they
 * must touch the panel: the active tab merges into its top line.
 */
export function PrerequisitesBanner({
  module,
  composed,
}: {
  module: string;
  composed: ComposedPrerequisites;
}) {
  const navigate = useNavigate();
  const { lead } = composed;

  if (lead === null) return null;

  return (
    <div className="pb-3">
      <NextActionAlert
        id={`${module}-prerequisites`}
        /*
          AMBER for both kinds. Something is genuinely in the way either way — a form that
          cannot be submitted, or a module whose whole job cannot be done — which is the test
          that separates this tone from the slate an unfilled card takes.
        */
        tone="amber"
        icon={<TriangleAlert className="h-4 w-4" aria-hidden="true" />}
        title={composed.title}
        description={composed.description}
        actionLabel={`Go to ${lead.label}`}
        onAction={() => navigate(lead.to)}
        /*
          THE LINK DROPS WITHOUT THE KEY AND THE REQUIREMENT IS STILL NAMED — so this is
          `actionPermission`, which gates the BUTTON, and not `permission`, which gates the
          whole strip.

          It was `permission` until 06 Oct 2026, which hid the banner from precisely the reader
          it exists for: somebody holding the module's own keys but not the catalog's met a
          disabled Create, no sentence anywhere on the page, and no way to find out that the
          module had never been set up. A reader must know WHY Create is disabled even when
          they are not the person who can fix it — sending them to a page that will bounce them
          is the thing worth withholding, not the explanation.
        */
        actionPermission={lead.permission}
      />
    </div>
  );
}
